import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/Button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Progress } from '../ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { 
  Shield,
  Users,
  Building2,
  Stethoscope,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Activity,
  Settings,
  Bell,
  Search,
  Filter,
  Download,
  Upload,
  Edit,
  Eye,
  Plus,
  UserCheck,
  ClipboardList,
  Calendar,
  DollarSign,
  Star,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Phone,
  FileText,
  Target,
  Award,
  Zap,
  Database,
  Server,
  Wifi,
  HardDrive,
  Cpu,
  MessageSquare,
  Globe,
  Lock,
  Key,
  Trash2,
  UserPlus,
  UserMinus
} from 'lucide-react';

// Mock data for admin dashboard
const systemStats = {
  totalUsers: 1247,
  activeUsers: 892,
  totalProviders: 45,
  totalFacilities: 78,
  totalReferrals: 2345,
  completedReferrals: 2103,
  systemUptime: 99.8,
  averageResponseTime: 1.2
};

const recentActivity = [
  {
    id: 1,
    type: 'user_registration',
    user: 'Dr. Emily Rodriguez',
    action: 'New provider registration',
    timestamp: '2024-01-23 2:45 PM',
    status: 'pending'
  },
  {
    id: 2,
    type: 'facility_onboard',
    user: 'Westside Care Center',
    action: 'Facility onboarding completed',
    timestamp: '2024-01-23 1:30 PM',
    status: 'completed'
  },
  {
    id: 3,
    type: 'system_alert',
    user: 'System',
    action: 'High referral volume detected',
    timestamp: '2024-01-23 11:15 AM',
    status: 'alert'
  }
];

const topProviders = [
  {
    id: 1,
    name: 'Dr. Sarah Smith',
    specialty: 'General Psychiatry',
    rating: 4.9,
    completedReferrals: 89,
    responseTime: '2.1 hours',
    status: 'active'
  },
  {
    id: 2,
    name: 'Dr. Michael Johnson',
    specialty: 'Geriatric Psychiatry',
    rating: 4.8,
    completedReferrals: 76,
    responseTime: '1.8 hours',
    status: 'active'
  },
  {
    id: 3,
    name: 'Dr. Lisa Chen',
    specialty: 'Child & Adolescent',
    rating: 4.7,
    completedReferrals: 54,
    responseTime: '3.2 hours',
    status: 'busy'
  }
];

const systemAlerts = [
  {
    id: 1,
    type: 'warning',
    title: 'High Referral Volume',
    message: 'Referral volume is 35% above average this week',
    timestamp: '2024-01-23 11:15 AM',
    severity: 'medium'
  },
  {
    id: 2,
    type: 'info',
    title: 'Scheduled Maintenance',
    message: 'System maintenance scheduled for Sunday 2:00 AM - 4:00 AM',
    timestamp: '2024-01-23 9:00 AM',
    severity: 'low'
  },
  {
    id: 3,
    type: 'success',
    title: 'Provider Onboarding',
    message: '3 new providers successfully onboarded this week',
    timestamp: '2024-01-22 4:30 PM',
    severity: 'low'
  }
];

export const AdminDashboard = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'busy': return 'bg-amber-100 text-amber-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      default: return 'bg-clinic-gray-100 text-clinic-gray-800';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-clinic-gray-100 text-clinic-gray-800';
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'warning': return <AlertCircle className="w-4 h-4 text-amber-600" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'success': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'info': return <Clock className="w-4 h-4 text-blue-600" />;
      default: return <AlertCircle className="w-4 h-4 text-clinic-gray-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-clinic-blue-50 to-clinic-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-clinic-gray-900 mb-2">
                Admin Dashboard
              </h1>
              <p className="text-lg text-clinic-gray-600">
                EnnVital Platform Management
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="outline" className="btn-md">
                <Bell className="w-4 h-4 mr-2" />
                Alerts (3)
              </Button>
              <Button variant="outline" className="btn-md">
                <Settings className="w-4 h-4 mr-2" />
                System Settings
              </Button>
            </div>
          </div>

          {/* System Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Total Users</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">{systemStats.totalUsers.toLocaleString()}</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      +12% this month
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-medical">
                    <Users className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Active Providers</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">{systemStats.totalProviders}</p>
                    <p className="text-xs text-blue-600 flex items-center mt-1">
                      <Stethoscope className="w-3 h-3 mr-1" />
                      {Math.round((systemStats.activeUsers / systemStats.totalUsers) * 100)}% active rate
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-medical">
                    <Stethoscope className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">Partner Facilities</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">{systemStats.totalFacilities}</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      +8% this quarter
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <Building2 className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-elevated">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-clinic-gray-600 mb-1">System Uptime</p>
                    <p className="text-3xl font-bold text-clinic-gray-900">{systemStats.systemUptime}%</p>
                    <p className="text-xs text-green-600 flex items-center mt-1">
                      <Activity className="w-3 h-3 mr-1" />
                      Excellent performance
                    </p>
                  </div>
                  <div className="icon-container icon-container-lg icon-success">
                    <Server className="icon-xl" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="providers">Providers</TabsTrigger>
            <TabsTrigger value="facilities">Facilities</TabsTrigger>
            <TabsTrigger value="system">System</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Recent Activity */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Activity className="icon-lg text-clinic-blue-600" />
                      Recent Activity
                    </CardTitle>
                    <CardDescription>Latest system events and user activities</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between p-4 bg-clinic-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="icon-container icon-container-sm icon-medical">
                            {activity.type === 'user_registration' && <UserPlus className="w-4 h-4" />}
                            {activity.type === 'facility_onboard' && <Building2 className="w-4 h-4" />}
                            {activity.type === 'system_alert' && <AlertCircle className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className="font-medium text-clinic-gray-900">{activity.user}</p>
                            <p className="text-sm text-clinic-gray-600">{activity.action}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={getStatusColor(activity.status)}>
                            {activity.status}
                          </Badge>
                          <p className="text-xs text-clinic-gray-500 mt-1">{activity.timestamp}</p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Referral Analytics */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <BarChart3 className="icon-lg text-clinic-blue-600" />
                      Referral Analytics
                    </CardTitle>
                    <CardDescription>Referral volume and completion metrics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-clinic-gray-900">2,345</p>
                        <p className="text-sm text-clinic-gray-600">Total Referrals</p>
                        <p className="text-xs text-green-600 mt-1">+18% vs last month</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-clinic-gray-900">2,103</p>
                        <p className="text-sm text-clinic-gray-600">Completed</p>
                        <p className="text-xs text-green-600 mt-1">89.7% completion rate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-clinic-gray-900">1.2h</p>
                        <p className="text-sm text-clinic-gray-600">Avg Response Time</p>
                        <p className="text-xs text-green-600 mt-1">Excellent performance</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                {/* System Alerts */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Bell className="icon-lg text-clinic-gold-600" />
                      System Alerts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {systemAlerts.map((alert) => (
                      <div key={alert.id} className="p-3 border rounded-lg">
                        <div className="flex items-start gap-2 mb-2">
                          {getAlertIcon(alert.type)}
                          <div className="flex-1">
                            <h4 className="font-medium text-clinic-gray-900 text-sm">{alert.title}</h4>
                            <Badge className={getSeverityColor(alert.severity) + ' text-xs'}>
                              {alert.severity}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-xs text-clinic-gray-600 mb-1">{alert.message}</p>
                        <p className="text-xs text-clinic-gray-500">{alert.timestamp}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Top Providers */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      <Award className="icon-lg text-clinic-gold-600" />
                      Top Providers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {topProviders.map((provider, index) => (
                      <div key={provider.id} className="flex items-center gap-3 p-3 bg-clinic-gray-50 rounded-lg">
                        <div className="text-center">
                          <div className="text-lg font-bold text-clinic-blue-600">#{index + 1}</div>
                        </div>
                        <Avatar className="w-8 h-8">
                          <AvatarFallback>{provider.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-medium text-clinic-gray-900 text-sm">{provider.name}</p>
                          <p className="text-xs text-clinic-gray-600">{provider.specialty}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 text-yellow-500" />
                              <span className="text-xs">{provider.rating}</span>
                            </div>
                            <Badge className={getStatusColor(provider.status) + ' text-xs'}>
                              {provider.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <Users className="icon-lg text-clinic-blue-600" />
                      User Management
                    </CardTitle>
                    <CardDescription>Manage platform users and permissions</CardDescription>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-clinic-gray-400 w-4 h-4" />
                      <Input placeholder="Search users..." className="pl-10 w-64" />
                    </div>
                    <Button className="btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="icon-container icon-container-2xl icon-medical mx-auto mb-4">
                    <Users className="icon-3xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-clinic-gray-900 mb-2">
                    User Management Interface
                  </h3>
                  <p className="text-clinic-gray-600 max-w-md mx-auto">
                    Comprehensive user management, role assignments, and permission controls will be available here.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Providers Tab */}
          <TabsContent value="providers" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <Stethoscope className="icon-lg text-clinic-blue-600" />
                      Provider Management
                    </CardTitle>
                    <CardDescription>Manage psychiatric providers and credentials</CardDescription>
                  </div>
                  <Button className="btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Onboard Provider
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="icon-container icon-container-2xl icon-medical mx-auto mb-4">
                    <Stethoscope className="icon-3xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-clinic-gray-900 mb-2">
                    Provider Management System
                  </h3>
                  <p className="text-clinic-gray-600 max-w-md mx-auto">
                    Provider onboarding, credential verification, performance tracking, and quality assurance tools.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Facilities Tab */}
          <TabsContent value="facilities" className="space-y-6">
            <Card className="card-elevated">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-3">
                      <Building2 className="icon-lg text-clinic-blue-600" />
                      Facility Management
                    </CardTitle>
                    <CardDescription>Manage partner healthcare facilities</CardDescription>
                  </div>
                  <Button className="btn-md bg-clinic-blue-600 hover:bg-clinic-blue-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Facility
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <div className="icon-container icon-container-2xl icon-medical mx-auto mb-4">
                    <Building2 className="icon-3xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-clinic-gray-900 mb-2">
                    Facility Partnership Hub
                  </h3>
                  <p className="text-clinic-gray-600 max-w-md mx-auto">
                    Facility onboarding, contract management, service agreements, and performance monitoring.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Server className="icon-lg text-clinic-blue-600" />
                    System Health
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-clinic-blue-600" />
                      <span className="text-sm">CPU Usage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">45%</span>
                      <Progress value={45} className="w-20 h-2" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-clinic-blue-600" />
                      <span className="text-sm">Memory Usage</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">62%</span>
                      <Progress value={62} className="w-20 h-2" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-clinic-blue-600" />
                      <span className="text-sm">Database Load</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">28%</span>
                      <Progress value={28} className="w-20 h-2" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wifi className="w-4 h-4 text-clinic-blue-600" />
                      <span className="text-sm">Network Status</span>
                    </div>
                    <Badge className="bg-green-100 text-green-800">Healthy</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Lock className="icon-lg text-clinic-gold-600" />
                    Security & Compliance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">HIPAA Compliance</span>
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">SSL Certificate</span>
                    <Badge className="bg-green-100 text-green-800">Valid</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Data Encryption</span>
                    <Badge className="bg-green-100 text-green-800">AES-256</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Last Security Audit</span>
                    <span className="text-sm text-clinic-gray-600">Dec 15, 2023</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Backup Status</span>
                    <Badge className="bg-green-100 text-green-800">Current</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <BarChart3 className="icon-lg text-clinic-blue-600" />
                    Platform Usage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Daily Active Users</span>
                      <span className="font-semibold">892</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Monthly Referrals</span>
                      <span className="font-semibold">456</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Completed Sessions</span>
                      <span className="font-semibold">1,234</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-clinic-gray-600">Platform Uptime</span>
                      <span className="font-semibold">99.8%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <PieChart className="icon-lg text-clinic-gold-600" />
                    User Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Patients</span>
                      <span className="text-sm font-medium">65%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Facilities</span>
                      <span className="text-sm font-medium">25%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Providers</span>
                      <span className="text-sm font-medium">8%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Admins</span>
                      <span className="text-sm font-medium">2%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-elevated">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Target className="icon-lg text-clinic-success-600" />
                    Performance KPIs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-clinic-gray-900">4.8</div>
                      <div className="text-sm text-clinic-gray-600">Avg Satisfaction</div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Response Time SLA</span>
                        <span className="text-green-600">Met</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Completion Rate</span>
                        <span className="text-green-600">89.7%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};